import { useEffect, useState } from "react";
import { ChevronDown, Layers } from "lucide-react";
import api from "@/lib/crmApi";
import { getSector, setSector } from "@/lib/sectorStore";
import { useAuth } from "@/context/CrmAuth";
import { cn } from "@/lib/utils";

/**
 * Filtre sectoriel global (admin / manager).
 *
 * Habillé comme un bouton d'en-tête (42 px, bordure #e3e3e3) autour d'un
 * <select> natif transparent : le menu déroulant reste celui du système, ce
 * qui marche partout (clavier, mobile) sans réécrire un composant.
 *
 * `dark` : variante pour une surface sombre (barre latérale, menu du compte).
 */
export default function SectorSwitcher({ dark = false, className }) {
  const { user } = useAuth();
  const [sectors, setSectors] = useState([]);
  const [value, setValue] = useState(getSector());

  useEffect(() => {
    if (!user || user.role === "commercial") return;
    api.get("/sectors").then(({ data }) => setSectors(data)).catch(() => {});
  }, [user]);

  if (!user || user.role === "commercial") return null;

  const onChange = (v) => {
    setValue(v);
    setSector(v);
    window.location.reload();
  };

  const active = value !== "all";

  return (
    <label
      className={cn(
        "relative flex h-[42px] cursor-pointer items-center gap-[7px] rounded-[11px] border px-3 text-[13px] font-medium transition-colors",
        "focus-within:outline focus-within:outline-2 focus-within:outline-offset-2",
        dark
          ? "border-[#22304a] bg-white/[.06] text-[#e2e8f0] hover:border-[#3d5170] focus-within:outline-[#4f9dff]"
          : "border-[#e3e3e3] bg-white text-[#303030] hover:bg-[#fafafa] focus-within:outline-[#005bd3]",
        className
      )}
      title="Filtrer par secteur d'activité"
    >
      <Layers size={15} className={cn("shrink-0", active ? (dark ? "text-[#7fb4ff]" : "text-[#005bd3]") : (dark ? "text-[#64748b]" : "text-[#64748b]"))} />
      <select
        data-testid="sector-switcher"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Secteur d'activité"
        className={cn(
          "min-w-0 flex-1 cursor-pointer appearance-none bg-transparent pr-5 text-[13px] outline-none",
          dark
            ? cn(active ? "font-semibold text-[#7fb4ff]" : "text-[#e2e8f0]", "[&>option]:bg-[#0f172a] [&>option]:text-[#e2e8f0]")
            : active ? "font-semibold text-[#00427a]" : "text-[#303030]"
        )}
      >
        <option value="all">Tous secteurs</option>
        {sectors.map((s) => (
          <option key={s.code} value={s.code}>{s.label} ({s.count})</option>
        ))}
      </select>
      <ChevronDown size={14} className={cn("pointer-events-none absolute right-3 shrink-0", dark ? "text-[#64748b]" : "text-[#94a3b8]")} />
    </label>
  );
}
